import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { StatusCodes } from 'http-status-codes';

import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import { ChatbotService, Conversation, ConversationSummary, MessageRole, QuickActionItem, SendMessageResponse, ServiceError } from "../../services/chatbot-service";
import chatbotConfig from "../../components/ChatbotWidget/chatbot-config.json"

export enum Sender {
    USER = "user",
    BOT = "bot",
}

export interface Message {
    id: string;
    text: string;
    sender: Sender;
    loading?: boolean;
}

interface ChatbotContextValue {
    messages: Message[];
    showPrompts: boolean;
    isLoading: boolean;
    transcriptLoading: boolean;
    sendMessage: (text: string) => void;
    setShowPrompts: (v: boolean) => void;
    activeConversationId: string | null;
    conversations: ConversationSummary[];
    conversationsLoading: boolean;
    conversationsInitializing: boolean;
    hasMoreConversations: boolean;
    historyAvailable: boolean;
    loadConversation: (id: string) => void;
    startNewConversation: () => void;
    deleteConversation: (id: string) => Promise<boolean>;
    refreshConversations: () => void;
    loadMoreConversations: () => void;
    initError: string | null;
    quickActions: QuickActionItem[];
    quickActionsLoading: boolean;
    quickActionsAvailable: boolean;
    saveQuickActions: (items: QuickActionItem[]) => Promise<boolean>;
    quickActionsVisible: boolean;
    toggleQuickActions: () => void;
    pendingConversationIds: string[];
    unreadConversationIds: string[];
}

const ChatbotContext = createContext<ChatbotContextValue | null>(null);
const INITIAL_MESSAGE = chatbotConfig.initialMessage as Message;
const SESSION_STORAGE_KEY = 'chatbot-active-conversation-id';
const QUICK_ACTIONS_VISIBLE_KEY = 'chatbot-quick-actions-visible';
const PENDING_MESSAGES_KEY = 'chatbot-pending-messages';
const UNREAD_CONVERSATIONS_KEY = 'chatbot-unread-conversations';
const PENDING_POLL_INTERVAL_MS = 3000;
const PENDING_MAX_AGE_MS = 2 * 60 * 1000;

// Any non-empty first user message becomes the conversation title; empty falls back to the untitled label
const TITLE_MIN_LENGTH = 1;
// Matches backend CONVERSATION.TITLE_MAX_LENGTH
const TITLE_MAX_LENGTH = 60;

const buildDefaultQuickActions = (): QuickActionItem[] =>
    chatbotConfig.supportPrompts.map((sp, index) => ({
        id: `default-${index + 1}`,
        title: sp.text,
        prompt: sp.query,
    }));

// Seeds the persisted quick-actions list from the static support prompts the first
// time a user has none. Returns the persisted list on success, or the local defaults
// when the save fails so the UI still renders something usable.
const seedDefaultQuickActions = async (): Promise<QuickActionItem[]> => {
    const defaults = buildDefaultQuickActions();
    try {
        return await ChatbotService.instance.saveQuickActions(defaults);
    } catch {
        return defaults;
    }
};

const isUnmodifiedSeed = (items: QuickActionItem[]): boolean =>
    items.every(
        (item) => item.id.startsWith('default-') && item.createdAt !== undefined && item.createdAt === item.updatedAt,
    );

interface PendingMessage {
    text: string;
    sentAt: number;
    priorMatches: number;
}

type PendingMessages = Record<string, PendingMessage>;

const readSessionJson = <T,>(key: string, fallback: T): T => {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
        return fallback;
    }
};

const writeSessionItem = (key: string, value: string | null) => {
    try {
        if (value === null) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, value);
        }
    } catch (error) {
        console.error(`Failed to write ${key} to session storage:`, error);
    }
};

const writeSessionJson = (key: string, value: unknown, isEmpty: boolean) =>
    writeSessionItem(key, isEmpty ? null : JSON.stringify(value));

const readPendingMessages = (): PendingMessages => readSessionJson<PendingMessages>(PENDING_MESSAGES_KEY, {});

const writePendingMessages = (pending: PendingMessages) =>
    writeSessionJson(PENDING_MESSAGES_KEY, pending, Object.keys(pending).length === 0);

const setPendingMessage = (conversationId: string, pending: PendingMessage) => {
    writePendingMessages({ ...readPendingMessages(), [conversationId]: pending });
};

const clearPendingMessage = (conversationId: string, sentAt: number) => {
    const all = readPendingMessages();
    if (all[conversationId]?.sentAt !== sentAt) return;
    delete all[conversationId];
    writePendingMessages(all);
};

const getActivePendingMessage = (conversationId: string): PendingMessage | undefined => {
    const pending = readPendingMessages()[conversationId];
    if (!pending) return undefined;
    if (Date.now() - pending.sentAt > PENDING_MAX_AGE_MS) {
        clearPendingMessage(conversationId, pending.sentAt);
        return undefined;
    }
    return pending;
};

const getActivePendingIds = (): string[] =>
    Object.keys(readPendingMessages()).filter((id) => getActivePendingMessage(id) !== undefined);

const withPendingOrder = (list: ConversationSummary[]): ConversationSummary[] =>
    list
        .map((c) => {
            const pending = getActivePendingMessage(c.conversationId);
            if (!pending) return c;
            return {
                ...c,
                updatedAt: Math.max(c.updatedAt, pending.sentAt),
                title: c.title || pending.text.slice(0, TITLE_MAX_LENGTH),
            };
        })
        .sort((a, b) => b.updatedAt - a.updatedAt);

const userMessageIndexes = (saved: Conversation['messages'], text: string): number[] =>
    saved.flatMap((msg, index) => (msg.role === MessageRole.USER && msg.content.trim() === text.trim() ? [index] : []));

const isRetryableSendError = (error: unknown): boolean =>
    !(error instanceof ServiceError) || error.statusCode >= StatusCodes.INTERNAL_SERVER_ERROR;

const isPendingAnswered = (saved: Conversation['messages'], pending: PendingMessage): boolean => {
    const matches = userMessageIndexes(saved, pending.text);
    if (matches.length <= (pending.priorMatches ?? 0)) {
        return false;
    }
    const latest = matches[matches.length - 1];
    return saved.slice(latest + 1).some((msg) => msg.role === MessageRole.ASSISTANT);
};

const readUnreadConversationIds = (): string[] => readSessionJson<string[]>(UNREAD_CONVERSATIONS_KEY, []);

const toMessages = (conversation: Conversation): Message[] => [
    INITIAL_MESSAGE,
    ...conversation.messages.map((msg) => ({
        id: crypto.randomUUID(),
        text: msg.content,
        sender: msg.role === MessageRole.USER ? Sender.USER : Sender.BOT,
    })),
];

const isSameTranscript = (a: Conversation['messages'], b: Conversation['messages']): boolean =>
    a.length === b.length && a.every((msg, i) => msg.role === b[i].role && msg.content === b[i].content);

const pendingBubbles = (text: string): Message[] => [
    { id: crypto.randomUUID(), text, sender: Sender.USER },
    { id: crypto.randomUUID(), text: "", sender: Sender.BOT, loading: true },
];

export const useChatbot = () => {
    const context = useContext(ChatbotContext);
    if (!context) throw new Error("useChatbot must be used within ChatbotProvider");
    return context;
};

export const ChatbotProvider = ({ children }: { children: React.ReactNode }) => {
    const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
    const [showPrompts, setShowPrompts] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [conversationsInitializing, setConversationsInitializing] = useState(true);
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const [historyAvailable, setHistoryAvailable] = useState(true);
    const [initError, setInitError] = useState<string | null>(null);
    const [quickActions, setQuickActions] = useState<QuickActionItem[]>([]);
    const [quickActionsLoading, setQuickActionsLoading] = useState(true);
    const [quickActionsAvailable, setQuickActionsAvailable] = useState(true);
    const [quickActionsVisible, setQuickActionsVisible] = useState(() => {
        const stored = sessionStorage.getItem(QUICK_ACTIONS_VISIBLE_KEY);
        return stored !== null ? stored === "true" : true;
    });
    const [pendingConversationIds, setPendingConversationIds] = useState<string[]>([]);
    const [unreadConversationIds, setUnreadConversationIds] = useState<string[]>(readUnreadConversationIds);
    const activeConversationIdRef = useRef(activeConversationId);
    activeConversationIdRef.current = activeConversationId;
    const requestIdRef = useRef(0);
    const messagesRef = useRef(messages);
    const nextTokenRef = useRef<string | undefined>(undefined);
    const initialLoadDoneRef = useRef(false);
    const quickActionsInitDoneRef = useRef(false);
    const openPollTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
    const backgroundTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
    const backgroundWatchesRef = useRef(new Map<string, symbol>());
    const conversationCacheRef = useRef(new Map<string, Conversation>());
    const cacheRefreshRef = useRef(new Map<string, Promise<void>>());
    const showPromptsRef = useRef(showPrompts);
    const conversationsRef = useRef(conversations);
    const mountedRef = useRef(true);
    messagesRef.current = messages;
    showPromptsRef.current = showPrompts;
    conversationsRef.current = conversations;

    const stopPendingPoll = useCallback(() => {
        openPollTimersRef.current.forEach(clearTimeout);
        openPollTimersRef.current.clear();
    }, []);

    useEffect(() => {
        const backgroundTimers = backgroundTimersRef.current;
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stopPendingPoll();
            backgroundTimers.forEach(clearTimeout);
            backgroundTimers.clear();
        };
    }, [stopPendingPoll]);

    const markPending = useCallback((id: string) => {
        setPendingConversationIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const unmarkPending = useCallback((id: string) => {
        setPendingConversationIds((prev) => prev.filter((pendingId) => pendingId !== id));
    }, []);

    const setUnread = useCallback((id: string, unread: boolean) => {
        setUnreadConversationIds((prev) => {
            if (prev.includes(id) === unread) return prev;
            const next = unread ? [...prev, id] : prev.filter((unreadId) => unreadId !== id);
            writeSessionJson(UNREAD_CONVERSATIONS_KEY, next, next.length === 0);
            return next;
        });
    }, []);

    const resolvePending = useCallback((id: string, sentAt?: number) => {
        if (sentAt !== undefined) {
            clearPendingMessage(id, sentAt);
        }
        unmarkPending(id);
    }, [unmarkPending]);

    const setActiveChat = useCallback((id: string | null) => {
        activeConversationIdRef.current = id;
        setActiveConversationId(id);
        writeSessionItem(SESSION_STORAGE_KEY, id);
    }, []);

    const bumpMessageCount = useCallback((id: string) => {
        setConversations((prev) => prev.map((c) => (c.conversationId === id
            ? { ...c, messageCount: c.messageCount + 2 }
            : c)));
    }, []);

    const addConversationToTop = useCallback((id: string, title: string, messageCount: number) => {
        setConversations((prev) => [{ conversationId: id, title, updatedAt: Date.now(), messageCount }, ...prev]);
    }, []);

    const fetchConversations = useCallback(async (append = false, silent = false) => {
        if (!silent) {
            setConversationsLoading(true);
        }
        try {
            const token = append ? nextTokenRef.current : undefined;
            const result = await ChatbotService.instance.getConversations(token);
            setConversations((prev) => append ? [...prev, ...result.conversations] : withPendingOrder(result.conversations));
            nextTokenRef.current = result.nextToken;
            setHasMoreConversations(!!result.nextToken);
            setHistoryAvailable(true);
        } catch (error: unknown) {
            if (error instanceof ServiceError && error.statusCode === StatusCodes.SERVICE_UNAVAILABLE) {
                setHistoryAvailable(false);
            }
        } finally {
            if (!silent) {
                setConversationsLoading(false);
            }
        }
    }, []);

    const pollPending = useCallback((
        id: string,
        timers: Set<ReturnType<typeof setTimeout>>,
        isCurrent: () => boolean,
        onSettled: (conversation: Conversation | undefined, answered: boolean, resolvedHere: boolean) => void,
    ) => {
        const initial = getActivePendingMessage(id);
        if (!initial) {
            unmarkPending(id);
            return;
        }

        const isLive = () => mountedRef.current && isCurrent();

        const schedule = () => {
            const timer = setTimeout(() => {
                timers.delete(timer);
                check();
            }, PENDING_POLL_INTERVAL_MS);
            timers.add(timer);
        };

        const check = async () => {
            if (!isLive()) return;
            const pending = getActivePendingMessage(id);
            try {
                const conversation = await ChatbotService.instance.getConversation(id);
                conversationCacheRef.current.set(id, conversation);
                if (!isLive()) return;
                const answered = isPendingAnswered(conversation.messages, pending ?? initial);
                if (pending && !answered) {
                    schedule();
                    return;
                }
                const stillPending = getActivePendingMessage(id);
                resolvePending(id, stillPending?.sentAt);
                onSettled(conversation, answered, stillPending !== undefined);
            } catch (error) {
                if (!isLive()) return;
                if (pending) {
                    schedule();
                    return;
                }
                console.error('Failed to refresh pending conversation:', error);
                resolvePending(id);
                onSettled(undefined, false, false);
            }
        };

        schedule();
    }, [resolvePending, unmarkPending]);

    const pollOpenConversation = useCallback((id: string, text: string, requestId: number) => {
        const priorMatches = getActivePendingMessage(id)?.priorMatches ?? 0;
        pollPending(id, openPollTimersRef.current, () => requestIdRef.current === requestId, (conversation, answered, resolvedHere) => {
            if (conversation) {
                const questionSaved = userMessageIndexes(conversation.messages, text).length > priorMatches;
                setMessages(answered
                    ? toMessages(conversation)
                    : [
                        ...toMessages(conversation),
                        ...(questionSaved ? [] : [{ id: crypto.randomUUID(), text, sender: Sender.USER }]),
                        { id: crypto.randomUUID(), text: getString('chatbot.messages.error'), sender: Sender.BOT },
                    ]);
            }
            if (answered && resolvedHere) {
                bumpMessageCount(id);
            }
            setIsLoading(false);
        });
    }, [bumpMessageCount, pollPending]);

    const watchPendingInBackground = useCallback((id: string) => {
        if (backgroundWatchesRef.current.has(id) || !getActivePendingMessage(id)) return;
        const watch = Symbol(id);
        backgroundWatchesRef.current.set(id, watch);
        markPending(id);
        const isCurrent = () => backgroundWatchesRef.current.get(id) === watch;
        pollPending(id, backgroundTimersRef.current, isCurrent, (_conversation, answered, resolvedHere) => {
            if (isCurrent()) backgroundWatchesRef.current.delete(id);
            if (!answered || !resolvedHere) return;
            if (activeConversationIdRef.current !== id) {
                setUnread(id, true);
            }
            bumpMessageCount(id);
        });
    }, [bumpMessageCount, markPending, pollPending, setUnread]);

    const showConversation = useCallback((id: string, conversation: Conversation, requestId: number) => {
        stopPendingPoll();
        setTranscriptLoading(false);
        const pending = getActivePendingMessage(id);
        const awaitingReply = pending !== undefined && !isPendingAnswered(conversation.messages, pending);
        if (pending && !awaitingReply) {
            resolvePending(id, pending.sentAt);
        }
        setMessages(awaitingReply ? [...toMessages(conversation), ...pendingBubbles(pending.text)] : toMessages(conversation));
        setIsLoading(awaitingReply);
        if (awaitingReply) {
            markPending(id);
            backgroundWatchesRef.current.delete(id);
            pollOpenConversation(id, pending.text, requestId);
        }
    }, [markPending, pollOpenConversation, resolvePending, stopPendingPoll]);

    const leaveOpenConversation = useCallback((nextId: string | null) => {
        stopPendingPoll();
        const leavingId = activeConversationIdRef.current;
        if (leavingId && leavingId !== nextId) {
            watchPendingInBackground(leavingId);
        }
    }, [stopPendingPoll, watchPendingInBackground]);

    const loadConversation = useCallback(async (id: string) => {
        const currentRequestId = ++requestIdRef.current;
        leaveOpenConversation(id);
        const previous = {
            messages: messagesRef.current,
            activeId: activeConversationIdRef.current,
            showPrompts: showPromptsRef.current,
            wasUnread: readUnreadConversationIds().includes(id),
        };

        setActiveChat(id);
        setShowPrompts(false);
        setUnread(id, false);

        const cached = conversationCacheRef.current.get(id);
        if (cached) {
            showConversation(id, cached, currentRequestId);
        } else {
            setMessages([INITIAL_MESSAGE]);
            setTranscriptLoading(true);
            setIsLoading(true);
        }

        try {
            const conversation = await ChatbotService.instance.getConversation(id);
            conversationCacheRef.current.set(id, conversation);
            if (requestIdRef.current !== currentRequestId) return;
            if (!cached || !isSameTranscript(cached.messages, conversation.messages)) {
                showConversation(id, conversation, currentRequestId);
            }
        } catch (error) {
            console.error('Failed to load conversation:', error);
            if (requestIdRef.current !== currentRequestId || cached) return;
            setUnread(id, previous.wasUnread);
            setActiveChat(previous.activeId);
            setShowPrompts(previous.showPrompts);
            const previousCached = previous.activeId ? conversationCacheRef.current.get(previous.activeId) : undefined;
            if (previous.activeId && previousCached) {
                showConversation(previous.activeId, previousCached, currentRequestId);
            } else {
                setMessages(previous.messages);
                setTranscriptLoading(false);
                setIsLoading(previous.messages.some((msg) => msg.loading));
            }
        }
    }, [leaveOpenConversation, setActiveChat, setUnread, showConversation]);

    const startNewConversation = useCallback(() => {
        requestIdRef.current++;
        leaveOpenConversation(null);
        setTranscriptLoading(false);
        setMessages([INITIAL_MESSAGE]);
        setActiveChat(null);
        setShowPrompts(true);
        setIsLoading(false);
    }, [leaveOpenConversation, setActiveChat]);

    const deleteConversation = useCallback(async (id: string): Promise<boolean> => {
        try {
            await ChatbotService.instance.deleteConversation(id);
            setConversations((prev) => prev.filter((c) => c.conversationId !== id));
            conversationCacheRef.current.delete(id);
            setUnread(id, false);
            if (activeConversationId === id) {
                startNewConversation();
            }
            return true;
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            return false;
        }
    }, [activeConversationId, setUnread, startNewConversation]);

    const refreshConversations = useCallback(() => {
        nextTokenRef.current = undefined;
        fetchConversations(false, true);
    }, [fetchConversations]);

    const loadMoreConversations = useCallback(() => {
        if (hasMoreConversations && !conversationsLoading) {
            fetchConversations(true);
        }
    }, [hasMoreConversations, conversationsLoading, fetchConversations]);

    const saveQuickActions = useCallback(async (items: QuickActionItem[]): Promise<boolean> => {
        try {
            const result = await ChatbotService.instance.saveQuickActions(items);
            setQuickActions(result);
            return true;
        } catch (error) {
            console.error('Failed to save quick actions:', error);
            return false;
        }
    }, []);

    const toggleQuickActions = useCallback(() => {
        setQuickActionsVisible((prev) => {
            const next = !prev;
            sessionStorage.setItem(QUICK_ACTIONS_VISIBLE_KEY, String(next));
            return next;
        });
    }, []);

    const discardEmptyConversation = useCallback((id: string) => {
        setConversations((prev) => prev.filter((c) => c.conversationId !== id));
        conversationCacheRef.current.delete(id);
        if (activeConversationIdRef.current === id) {
            setActiveChat(null);
        }
        ChatbotService.instance.deleteConversation(id).catch((error: unknown) => {
            console.error('Failed to delete empty conversation:', error);
        });
    }, [setActiveChat]);

    const sendMessage = useCallback(async (text: string) => {
        const userMsgId = crypto.randomUUID();
        const botMsgId = crypto.randomUUID();

        setMessages((prev) => [
            ...prev,
            { id: userMsgId, text, sender: Sender.USER },
            { id: botMsgId, text: "", sender: Sender.BOT, loading: true },
        ]);
        setIsLoading(true);
        setShowPrompts(false);
        stopPendingPoll();
        const currentRequestId = ++requestIdRef.current;

        // Capture history before any await so messagesRef.current
        // still reflects the state prior to the setMessages above.
        // conversationHistory excludes the current user message — the backend
        // receives it via the top-level `message` field in the request body.
        const conversationHistory = messagesRef.current
            .filter((msg) => msg.id !== "initial-message" && !msg.loading)
            .map((msg) => ({ text: msg.text, sender: msg.sender }));

        const updateBotMessage = (responseText: string) => {
            setMessages((prev) => {
                const messageIndex = prev.findIndex((msg) => msg.id === botMsgId);
                if (messageIndex === -1) return prev;
                const updated = [...prev];
                updated[messageIndex] = { ...prev[messageIndex], text: responseText, loading: false };
                return updated;
            });
            setIsLoading(false);
        };

        const title = text.length >= TITLE_MIN_LENGTH ? text.slice(0, TITLE_MAX_LENGTH) : "";
        let conversationId = activeConversationId;
        let createdForThisSend = false;
        if (!conversationId && historyAvailable) {
            try {
                const created = await ChatbotService.instance.createConversation();
                conversationId = created.conversationId;
                createdForThisSend = true;
                const newId = created.conversationId;
                addConversationToTop(newId, title, 0);
                conversationCacheRef.current.set(newId, { conversationId: newId, title, messages: [], updatedAt: Date.now() });
                if (requestIdRef.current === currentRequestId) {
                    setActiveChat(newId);
                }
            } catch (error) {
                console.error('Failed to create conversation before sending:', error);
            }
        }

        const sentAt = Date.now();
        const previousUpdatedAt = conversationsRef.current.find((c) => c.conversationId === conversationId)?.updatedAt;
        if (conversationId) {
            const sentId = conversationId;
            await cacheRefreshRef.current.get(sentId);
            if (!conversationCacheRef.current.has(sentId)) {
                try {
                    conversationCacheRef.current.set(sentId, await ChatbotService.instance.getConversation(sentId));
                } catch (error) {
                    console.error('Failed to load conversation before sending:', error);
                }
            }
            const savedMessages = conversationCacheRef.current.get(sentId)?.messages ?? [];
            setPendingMessage(sentId, { text, sentAt, priorMatches: userMessageIndexes(savedMessages, text).length });
            markPending(sentId);
            setConversations((prev) => prev
                .map((c) => (c.conversationId === sentId ? { ...c, updatedAt: sentAt } : c))
                .sort((a, b) => b.updatedAt - a.updatedAt));
        }

        try {
            const result: SendMessageResponse = await ChatbotService.instance.sendMessage(
                text, conversationHistory, conversationId ?? undefined,
            );
            const handledHere = conversationId !== null && readPendingMessages()[conversationId]?.sentAt === sentAt;
            if (conversationId) {
                resolvePending(conversationId, sentAt);
                if (handledHere && activeConversationIdRef.current !== conversationId) {
                    setUnread(conversationId, true);
                }
                const answeredId = conversationId;
                const refresh = ChatbotService.instance.getConversation(answeredId)
                    .then((saved) => { conversationCacheRef.current.set(answeredId, saved); })
                    .catch(() => { conversationCacheRef.current.delete(answeredId); })
                    .finally(() => {
                        if (cacheRefreshRef.current.get(answeredId) === refresh) {
                            cacheRefreshRef.current.delete(answeredId);
                        }
                    });
                cacheRefreshRef.current.set(answeredId, refresh);
            }

            if (result.conversationMode === "created" && result.conversationId) {
                addConversationToTop(result.conversationId, title, 2);
                if (requestIdRef.current === currentRequestId) {
                    setActiveChat(result.conversationId);
                }
            } else if (result.conversationMode === "persistent" && conversationId && handledHere) {
                bumpMessageCount(conversationId);
            }

            if (requestIdRef.current !== currentRequestId) return;
            updateBotMessage(result.response);
        } catch (error) {
            if (conversationId && isRetryableSendError(error)) {
                if (requestIdRef.current === currentRequestId) {
                    pollOpenConversation(conversationId, text, currentRequestId);
                } else {
                    watchPendingInBackground(conversationId);
                }
                return;
            }
            if (conversationId) {
                resolvePending(conversationId, sentAt);
            }
            if (conversationId && createdForThisSend) {
                discardEmptyConversation(conversationId);
            } else if (conversationId && previousUpdatedAt !== undefined) {
                const failedId = conversationId;
                const restoreTo = previousUpdatedAt;
                setConversations((prev) => prev
                    .map((c) => (c.conversationId === failedId && c.updatedAt === sentAt ? { ...c, updatedAt: restoreTo } : c))
                    .sort((a, b) => b.updatedAt - a.updatedAt));
            }
            if (requestIdRef.current !== currentRequestId) return;
            updateBotMessage(getString('chatbot.messages.error'));
        }
    }, [
        activeConversationId, addConversationToTop, bumpMessageCount, discardEmptyConversation, historyAvailable,
        markPending, pollOpenConversation, resolvePending, setActiveChat, setUnread, stopPendingPoll, watchPendingInBackground,
    ]);

    // On mount: load conversations list, restore active conversation from sessionStorage
    useEffect(() => {
        if (initialLoadDoneRef.current) return;
        initialLoadDoneRef.current = true;

        const init = async () => {
            try {
                const result = await ChatbotService.instance.getConversations();
                setConversations(withPendingOrder(result.conversations));
                nextTokenRef.current = result.nextToken;
                setHasMoreConversations(!!result.nextToken);
                setHistoryAvailable(true);

                const activeId = sessionStorage.getItem(SESSION_STORAGE_KEY);
                const pendingIds = getActivePendingIds();
                pendingIds.filter((id) => id !== activeId).forEach(watchPendingInBackground);
                if (activeId && pendingIds.includes(activeId)) {
                    markPending(activeId);
                    loadConversation(activeId);
                } else {
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                }
            } catch (error: unknown) {
                console.error('Failed to initialize conversations:', error);
                setInitError(getString('chatbot.errors.initConversations'));
                if (error instanceof ServiceError && error.statusCode === StatusCodes.SERVICE_UNAVAILABLE) {
                    setHistoryAvailable(false);
                }
            } finally {
                setConversationsInitializing(false);
            }
        };

        init();
    }, [loadConversation, markPending, watchPendingInBackground]);

    useEffect(() => {
        if (quickActionsInitDoneRef.current) return;
        quickActionsInitDoneRef.current = true;

        const initQuickActions = async () => {
            try {
                const items = await ChatbotService.instance.getQuickActions();
                if (items.length === 0) {
                    setQuickActions(await seedDefaultQuickActions());
                } else {
                    setQuickActions(isUnmodifiedSeed(items) ? buildDefaultQuickActions() : items);
                }
            } catch (error: unknown) {
                console.error('Failed to load quick actions:', error);
                if (error instanceof ServiceError && error.statusCode === StatusCodes.SERVICE_UNAVAILABLE) {
                    setQuickActionsAvailable(false);
                }
            } finally {
                setQuickActionsLoading(false);
            }
        };

        initQuickActions();
    }, []);

    const value = useMemo<ChatbotContextValue>(
        () => ({
            messages,
            showPrompts,
            isLoading,
            transcriptLoading,
            sendMessage,
            setShowPrompts,
            activeConversationId,
            conversations,
            conversationsLoading,
            conversationsInitializing,
            hasMoreConversations,
            historyAvailable,
            loadConversation,
            startNewConversation,
            deleteConversation,
            refreshConversations,
            loadMoreConversations,
            initError,
            quickActions,
            quickActionsLoading,
            quickActionsAvailable,
            saveQuickActions,
            quickActionsVisible,
            toggleQuickActions,
            pendingConversationIds,
            unreadConversationIds,
        }),
        [
            messages,
            showPrompts,
            isLoading,
            transcriptLoading,
            sendMessage,
            setShowPrompts,
            activeConversationId,
            conversations,
            conversationsLoading,
            conversationsInitializing,
            hasMoreConversations,
            historyAvailable,
            loadConversation,
            startNewConversation,
            deleteConversation,
            refreshConversations,
            loadMoreConversations,
            initError,
            quickActions,
            quickActionsLoading,
            quickActionsAvailable,
            saveQuickActions,
            quickActionsVisible,
            toggleQuickActions,
            pendingConversationIds,
            unreadConversationIds,
        ]
    );

    return (
        <ChatbotContext.Provider value={value}>
            {children}
        </ChatbotContext.Provider>
    );
};
